/**
 * OpsIQ Session Manager - Wrapper around sessionManager with OpsIQ-specific storage prefix
 * Uses `opsiq_sessions_` prefix instead of `mentormind_sessions_`
 */

import {
  getAllSessions as baseGetAllSessions,
  createSession as baseCreateSession,
  getSession as baseGetSession,
  getCurrentSession as baseGetCurrentSession,
  setCurrentSession as baseSetCurrentSession,
  saveSession as baseSaveSession,
  updateSession as baseUpdateSession,
  addMessageToSession as baseAddMessageToSession,
  addInsightToSession as baseAddInsightToSession,
  updateSessionContext as baseUpdateSessionContext,
  listSessions as baseListSessions,
  deleteSession as baseDeleteSession,
  clearAllSessions as baseClearAllSessions,
} from './sessionManager';

/**
 * Get storage key for OpsIQ company (uses opsiq_sessions_ prefix)
 */
const getOpsIQStorageKey = (companyIdSlug) => {
  return `opsiq_sessions_${companyIdSlug || 'default'}`;
};

// Wrapper functions that use OpsIQ storage key
export const getAllSessions = (companyIdSlug) => {
  // Temporarily override getStorageKey by passing a normalized slug
  // We'll need to access the underlying storage directly
  try {
    const key = getOpsIQStorageKey(companyIdSlug);
    const data = localStorage.getItem(key);
    if (!data) return { sessions: [], currentSessionId: null };
    const parsed = JSON.parse(data);
    
    // Filter expired sessions (7-day TTL)
    const now = Date.now();
    const allSessions = parsed.sessions || [];
    const validSessions = allSessions.filter(s => {
      if (!s.expiresAt) return true; // Legacy sessions without expiry
      return s.expiresAt > now;
    });
    
    // If sessions were filtered, save the cleaned list
    if (validSessions.length !== allSessions.length) {
      try {
        localStorage.setItem(key, JSON.stringify({
          sessions: validSessions,
          currentSessionId: parsed.currentSessionId || null
        }));
      } catch (e) {
        // Failed to save filtered sessions
      }
    }
    
    return {
      sessions: validSessions,
      currentSessionId: parsed.currentSessionId || null
    };
  } catch (e) {
    console.error('[OpsIQSessionManager] Error getting sessions:', e);
    return { sessions: [], currentSessionId: null };
  }
};

export const createSession = (companyIdSlug, metadata = {}) => {
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const now = Date.now();
  const SESSION_TTL_DAYS = 7;
  
  const generateSessionTitle = (firstMessage, frameworkName) => {
    if (frameworkName) return `${frameworkName} Session`;
    if (firstMessage) {
      const text = firstMessage.content || firstMessage.text || '';
      if (text.length > 0) {
        const preview = text.substring(0, 50).trim();
        if (preview.length > 0) {
          return preview.charAt(0).toUpperCase() + preview.slice(1);
        }
      }
    }
    const date = new Date();
    return `Session ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };
  
  const session = {
    id: sessionId,
    title: metadata.title || generateSessionTitle(metadata.firstMessage, metadata.frameworkName),
    createdAt: now,
    updatedAt: now,
    expiresAt: now + (SESSION_TTL_DAYS * 24 * 60 * 60 * 1000),
    framework: metadata.framework || null,
    frameworkName: metadata.frameworkName || null,
    messageCount: 0,
    messages: [],
    insights: [],
    sources: [],
    context: {},
    metadata: {
      timeSaved: 0,
      duration: 0,
      ...metadata.metadata
    }
  };

  // Save to storage
  const { sessions } = getAllSessions(companyIdSlug);
  sessions.unshift(session);
  const limitedSessions = sessions.slice(0, 50);
  
  try {
    const key = getOpsIQStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions: limitedSessions,
      currentSessionId: sessionId
    }));
  } catch (e) {
    console.error('[OpsIQSessionManager] Error creating session:', e);
    if (e.name === 'QuotaExceededError') {
      const reduced = limitedSessions.slice(0, 25);
      try {
        localStorage.setItem(key, JSON.stringify({
          sessions: reduced,
          currentSessionId: sessionId
        }));
      } catch (e2) {
        console.error('[OpsIQSessionManager] Failed to save even with reduced sessions:', e2);
      }
    }
  }

  return session;
};

export const getSession = (companyIdSlug, sessionId) => {
  const { sessions } = getAllSessions(companyIdSlug);
  return sessions.find(s => s.id === sessionId) || null;
};

export const getCurrentSession = (companyIdSlug) => {
  const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
  if (!currentSessionId) return null;
  return sessions.find(s => s.id === currentSessionId) || null;
};

export const setCurrentSession = (companyIdSlug, sessionId) => {
  try {
    const key = getOpsIQStorageKey(companyIdSlug);
    const data = getAllSessions(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      ...data,
      currentSessionId: sessionId
    }));
  } catch (e) {
    console.error('[OpsIQSessionManager] Error setting current session:', e);
  }
};

export const saveSession = (companyIdSlug, sessionId, updates) => {
  try {
    const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
    const index = sessions.findIndex(s => s.id === sessionId);
    
    if (index === -1) {
      // Session not found
      return null;
    }

    const updatedSession = {
      ...sessions[index],
      ...updates,
      updatedAt: Date.now()
    };

    sessions[index] = updatedSession;

    const key = getOpsIQStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions,
      currentSessionId
    }));

    return updatedSession;
  } catch (e) {
    console.error('[OpsIQSessionManager] Error saving session:', e);
    return null;
  }
};

export const updateSession = (companyIdSlug, sessionId, updates) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  const merged = {
    ...session,
    ...updates,
    metadata: {
      ...session.metadata,
      ...(updates.metadata || {})
    },
    context: {
      ...session.context,
      ...(updates.context || {})
    },
    updatedAt: Date.now()
  };

  return saveSession(companyIdSlug, sessionId, merged);
};

export const addMessageToSession = (companyIdSlug, sessionId, message) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  const updatedMessages = [...(session.messages || []), message];
  
  return updateSession(companyIdSlug, sessionId, {
    messages: updatedMessages,
    messageCount: updatedMessages.length
  });
};

export const addInsightToSession = (companyIdSlug, sessionId, insight) => {
  const session = getSession(companyIdSlug, sessionId);
  if (!session) return null;

  const updatedInsights = [...(session.insights || []), insight];
  
  return updateSession(companyIdSlug, sessionId, {
    insights: updatedInsights
  });
};

export const updateSessionContext = (companyIdSlug, sessionId, context) => {
  return updateSession(companyIdSlug, sessionId, {
    context: {
      ...getSession(companyIdSlug, sessionId)?.context,
      ...context
    }
  });
};

export const listSessions = (companyIdSlug, limit = 10) => {
  const { sessions } = getAllSessions(companyIdSlug);
  const sorted = [...sessions].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  return sorted.slice(0, limit);
};

export const deleteSession = (companyIdSlug, sessionId) => {
  try {
    const { sessions, currentSessionId } = getAllSessions(companyIdSlug);
    const filtered = sessions.filter(s => s.id !== sessionId);
    
    const key = getOpsIQStorageKey(companyIdSlug);
    localStorage.setItem(key, JSON.stringify({
      sessions: filtered,
      currentSessionId: currentSessionId === sessionId ? null : currentSessionId
    }));

    return true;
  } catch (e) {
    console.error('[OpsIQSessionManager] Error deleting session:', e);
    return false;
  }
};

export const clearAllSessions = (companyIdSlug) => {
  try {
    const key = getOpsIQStorageKey(companyIdSlug);
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.error('[OpsIQSessionManager] Error clearing sessions:', e);
    return false;
  }
};

/**
 * Wrapper: List all OpsIQ sessions (no limit)
 * Used by OpsVoiceWidget for backward compatibility
 */
export const listOpsSessions = (companyIdSlug) => {
  const { sessions } = getAllSessions(companyIdSlug);
  return sessions || [];
};

/**
 * Wrapper: Save OpsIQ session and return updated sessions list
 * Used by OpsVoiceWidget for backward compatibility
 * @param {string} companyIdSlug - Company identifier
 * @param {object} sessionObj - Session object to save (may have different structure)
 * @returns {array} Updated sessions list
 */
export const saveOpsSession = (companyIdSlug, sessionObj) => {
  try {
    const sessionId = sessionObj.id;
    
    // Get existing session or create new
    const existingSession = sessionId ? getSession(companyIdSlug, sessionId) : null;
    
    if (existingSession) {
      // Update existing session - merge with existing data
      updateSession(companyIdSlug, sessionId, {
        title: sessionObj.title || existingSession.title,
        messages: sessionObj.messagesSnapshot || existingSession.messages || [],
        messageCount: (sessionObj.messagesSnapshot || []).length || existingSession.messageCount || 0,
      });
    } else {
      // Create new session - createSession generates its own ID, then we'll update if needed
      const newSession = createSession(companyIdSlug, {
        title: sessionObj.title || 'New Session',
        firstMessage: sessionObj.messagesSnapshot && sessionObj.messagesSnapshot[0] ? {
          content: sessionObj.messagesSnapshot[0].content || sessionObj.messagesSnapshot[0].text || ''
        } : null,
        metadata: {
          mode: sessionObj.mode || 'text',
          vertical: sessionObj.vertical,
        }
      });
      
      // Update with messages if provided
      if (sessionObj.messagesSnapshot && sessionObj.messagesSnapshot.length > 0) {
        updateSession(companyIdSlug, newSession.id, {
          messages: sessionObj.messagesSnapshot,
          messageCount: sessionObj.messagesSnapshot.length,
        });
      }
      
      // If widget provided a specific ID and it doesn't match, we can't use it
      // (createSession generates its own). This is expected behavior.
    }

    // Return updated sessions list
    return listOpsSessions(companyIdSlug);
  } catch (e) {
    console.error('[OpsIQSessionManager] Error in saveOpsSession:', e);
    return listOpsSessions(companyIdSlug);
  }
};
